import React, { useState } from 'react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import { Badge } from 'src/shared/components/ui/badge';
import { Loader } from 'src/shared/components/loader/Loader';
import ViewAllButton from 'src/shared/components/view-all-button/ViewAllButton';
import ProtectedAreasModal from './ProtectedAreasModal';
import { describeOverlap } from '../../state/api/overlapDisplay';
import { useGetSiteProtectedAreasQuery } from '../../state/api/siteNatureRiskEngineSlice';

type ProtectedAreasWidgetProps = {
    organisationBusinessCentralId: string;
    siteId: string;
    /** Selected time period — the list reflects the overlaps recorded then. */
    at?: string;
};

// Preview count — the engine returns the alphabetical top of the list.
const PREVIEW_SIZE = 10;

const ProtectedAreasWidget = ({ organisationBusinessCentralId, siteId, at }: ProtectedAreasWidgetProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const { data, isLoading, isError } = useGetSiteProtectedAreasQuery(
        { organisationBusinessCentralId, siteId, at, page: 1, pageSize: PREVIEW_SIZE },
        { skip: !organisationBusinessCentralId || !siteId }
    );

    if (isLoading) {
        return (
            <SummaryCard
                cardIcon={null}
                containerClasses='min-w-0 gap-4 p-5'
                cardTitle='Protected areas list'
                cardBody={
                    <div className='flex min-h-[120px] items-center justify-center'>
                        <Loader />
                    </div>
                }
            />
        );
    }

    // Shown rather than hidden: the widget disappears when a site has no coverage,
    // and a read that failed must not look like the same settled answer.
    if (isError) {
        return (
            <SummaryCard
                cardIcon={null}
                containerClasses='min-w-0 gap-4 p-5'
                cardTitle='Protected areas list'
                cardBody={
                    <p data-testid='protected-areas-list-error' className='text-sm text-grey-80'>
                        Protected areas could not be loaded. Try again shortly.
                    </p>
                }
            />
        );
    }

    const areas = data?.site?.protectedAreas.items ?? [];
    const total = data?.site?.protectedAreas.pagination.totalItems ?? 0;
    const hasProtectedAreasCoverage = Boolean(data?.site?.hasProtectedAreasCoverage);

    // When a site lacks protected areas coverage, the summary card displays a callout
    // explaining the gap.
    // With no nearby areas to display, there's nothing to show in the widget itself.
    if (!hasProtectedAreasCoverage && areas.length === 0) {
        return null;
    }

    return (
        <>
            <SummaryCard
                cardIcon={null}
                containerClasses='min-w-0 gap-4 p-5'
                cardTitle={
                    <span className='flex items-center gap-2'>
                        Protected areas list
                        <Badge variant='secondary' data-testid='protected-areas-count'>
                            {total}
                        </Badge>
                    </span>
                }
                action={
                    // Always offered, as the species widget beside it is.
                    <ViewAllButton ariaLabel='View all protected areas' showPanelIcon={false} onClick={() => setModalOpen(true)} />
                }
                cardBody={
                    <div className='flex flex-col gap-4'>
                        {areas.length > 0 && (
                            <div data-testid='protected-areas-widget' className='flex flex-col gap-4'>
                                <div className='flex justify-between text-sm font-medium text-black'>
                                    <span>Area Name</span>
                                    <span>Relationship</span>
                                </div>
                                <ul className='flex flex-col'>
                                    {areas.map(area => (
                                        <li
                                            key={area.name}
                                            className='flex items-center justify-between gap-4 border-t border-grey-20 py-3'
                                        >
                                                {/* `min-w-0` is what lets `truncate` bite: a flex
                                                item defaults to `min-width: auto`, so a long
                                                name would otherwise widen the row instead of
                                                being clipped. The full name stays on hover. */}
                                            <span className='min-w-0 flex-1 truncate' title={area.name}>
                                                {area.name}
                                            </span>
                                            <span className='shrink-0 whitespace-nowrap text-sm text-grey-80'>
                                                {describeOverlap(area.overlapType)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {areas.length === 0 && hasProtectedAreasCoverage && (
                            <p data-testid='protected-areas-empty' className='text-sm text-grey-80'>
                                No protected areas overlap or sit adjacent to this site.
                            </p>
                        )}
                    </div>
                }
            />

            {modalOpen && (
                <ProtectedAreasModal
                    onClose={() => setModalOpen(false)}
                    organisationBusinessCentralId={organisationBusinessCentralId}
                    siteId={siteId}
                    at={at}
                />
            )}
        </>
    );
};

export default ProtectedAreasWidget;
